import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createApiKey_api, ApiKeyResponse, deleteApiKey_api, getApiKeys_api } from '../api/apiKeyApi';
import * as Sentry from '@sentry/browser';
import useBrand from './useBrand';

interface ApiKeyState {
    // state
    apiKeys: ApiKeyResponse[];
    isLoadingApiKey: boolean;

    // actions
    setApiKeyData: (apiKeyData: ApiKeyResponse[]) => void;

    // methods
    getApiKeys: ({ userId }: { userId?: number }) => Promise<ApiKeyResponse[] | null>;
    createApiKey: ({ userId }: { userId?: number }) => Promise<ApiKeyResponse | null>;
    deleteApiKey: ({ key }: { key?: string }) => Promise<ApiKeyResponse | null>;
}

const useApiKey = create(immer<ApiKeyState>((set, get) => ({
    // state
    apiKeys: [],
    isLoadingApiKey: false,

    // actions
    setApiKeyData: (apiKeyData: ApiKeyResponse[]) => set({ apiKeys: apiKeyData }),

    // methods
    getApiKeys: async ({ userId }: { userId?: number }) => {
        if (!userId) {
            return null;
        }

        set({ isLoadingApiKey: true });

        try {
            const apiKeyData = await getApiKeys_api({ userId });
            set({ apiKeys: apiKeyData || [] });
            return apiKeyData;
        } catch (error) {

            Sentry.captureException(error);
            return null;
        } finally {
            set({ isLoadingApiKey: false });
        }
    },

    createApiKey: async ({ userId }: { userId?: number }) => {
        const { apiKeys: apiKeyData } = get();
        const { currentBrand } = useBrand.getState();
        if (!userId) {
            return null;
        }

        set({ isLoadingApiKey: true });
        try {
            const apiKeyCreated = await createApiKey_api({ userId, brandId: currentBrand?.id });

            set({
                apiKeys: [...apiKeyData, apiKeyCreated as ApiKeyResponse],
            });

            return apiKeyCreated;
        } catch (error) {
            Sentry.captureException(error);
            return null;
        } finally {
            set({ isLoadingApiKey: false });
        }
    },

    deleteApiKey: async ({ key }: { key?: string }) => {
        const { apiKeys } = get();
        if (!key) {
            return null;
        }

        try {
            set({ isLoadingApiKey: true });

            const apiKeyDeleted = await deleteApiKey_api({ key });
            set({ apiKeys: apiKeys.filter((apiKey) => apiKey.key !== key) });

            return apiKeyDeleted;
        } catch (error) {
            Sentry.captureException(error);
            return null;
        } finally {
            set({ isLoadingApiKey: false });
        }
    },
})));

export default useApiKey;

